// @flow
/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import Card from '../../components/Card';
import styles from './styles.module.scss';
import { H4, H6, Switch } from '../../components';
import Toggler from '../../components/Toggler';
import Select from '../../components/Select';
import Multicheckbox from '../../components/Multicheckbox';
import ChallengeSaveHint from './ChallengeSaveHint';

type Props = {
  tableSize: string,
  setTableSize: () => void,
  tableSizeOptions: Array<any>,
  stackSize: string,
  setStackSize: () => void,
  stackSizeOptions: Array<any>,
  positionType: string,
  setPositionType: () => void,
  positionTypeOptions: Array<any>,
  position: string,
  setPosition: () => void,
  positionOptions: Array<any>,
  resultsPositionType: any,
  resultsPositionTypeOptions: Array<any>,
  setResultsPositionType: () => void,
  resultsPfrType: any,
  resultsPfrTypeOptions: Array<any>,
  setResultsPfrType: () => void,
  resultsPlayerPosition: any,
  resultsPlayerPositionOptions: Array<any>,
  setResultsPlayerPosition: () => void,
  resultsPosition: any,
  resultsPositionOptions: Array<any>,
  setResultsPosition: () => void,
  boardType: string,
  setBoardType: () => void,
  boardTypeOptions: Array<any>,
  boardSuit: string,
  setBoardSuit: () => void,
  boardSuitOptions: Array<any>,
  selectedBoard: string,
  setSelectedBoard: () => void,
  boardOptions: Array<any>,
  selectedHighCard: string,
  setSelectedHighCard: () => void,
  highCardOptions: Array<any>,
  selectedHandType: any,
  setSelectedHandType: () => void,
  handTypeOptions: Array<any>,
  pageMode: string,
  pageModeOptions: Array<any>,
  setPageMode: () => void,
  disabled?: boolean,
};

const ControlPanel = ({
  tableSize,
  setTableSize,
  tableSizeOptions,
  stackSize,
  setStackSize,
  stackSizeOptions,
  positionType,
  positionTypeOptions,
  setPositionType,
  position,
  setPosition,
  positionOptions,
  resultsPositionType,
  resultsPositionTypeOptions,
  setResultsPositionType,
  resultsPfrType,
  resultsPfrTypeOptions,
  setResultsPfrType,
  resultsPlayerPosition,
  resultsPlayerPositionOptions,
  setResultsPlayerPosition,
  resultsPosition,
  resultsPositionOptions,
  setResultsPosition,
  boardType,
  boardTypeOptions,
  setBoardType,
  boardSuit,
  boardSuitOptions,
  setBoardSuit,
  selectedBoard,
  setSelectedBoard,
  boardOptions,
  selectedHighCard,
  setSelectedHighCard,
  highCardOptions,
  selectedHandType,
  setSelectedHandType,
  handTypeOptions,
  pageMode,
  setPageMode,
  pageModeOptions = [],
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const localizedPageModeOptions = pageModeOptions.map(option => option.value === 'Results'
    ? {
      ...option,
      label: t(['challengeLabel', 'Challenge']),
      textAdd: t(['savingResultsLabel', '(Saving Results)']),
    }
    : option);
  const localizedPageMode = localizedPageModeOptions.find(option => option.value === pageMode?.value)
    || pageMode;
  const showHandTypeSelect = ['Training', 'Results'].includes(pageMode?.value);
  const isResultsMode = pageMode?.value === 'Results';
  const panelRef = useRef(null);
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  const guardChange = (handler) => (...args) => {
    if (!disabledRef.current && handler) handler(...args);
  };
  const blockLockedEvent = (event) => {
    if (disabledRef.current) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const blockLockedFocus = (event) => {
    if (disabledRef.current) {
      event.target.blur();
      event.stopPropagation();
    }
  };
  useEffect(() => {
    if (disabled && panelRef.current?.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }, [disabled]);
  const selectedPositionOption = positionOptions.find((item) => item.value === position?.[0]) || null;
  const isChecked = () => {
    return boardOptions.filter(el => el.value === selectedBoard)
  }

  return (
    <Card
      className={classnames(styles.controlPanelWrapper, {
        [styles.controlPanelWrapperDisabled]: disabled,
      })}
    >
      {disabled ? <div className={styles.controlPanelOverlay} /> : null}
      <ChallengeSaveHint />
      <fieldset
        ref={panelRef}
        className={styles.controlPanelWrap}
        disabled={disabled}
        inert={disabled ? '' : undefined}
        aria-label="Flop settings"
        aria-disabled={disabled}
        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
        onClickCapture={blockLockedEvent}
        onMouseDownCapture={blockLockedEvent}
        onKeyDownCapture={blockLockedEvent}
        onFocusCapture={blockLockedFocus}
      >
        <div className={classnames(styles.controlPanelLimit, styles.control)}>
          <H4>Mode</H4>
          <Toggler
            selected={localizedPageMode}
            options={localizedPageModeOptions}
            onToggle={guardChange(setPageMode)}
            disabled={disabled}
            className={classnames(styles.gameTogglerColor, styles.firstToggler)}
            controlToggler
          />
          <Select
            className={styles.adaptiveSelectComponent}
            value={localizedPageMode}
            options={localizedPageModeOptions}
            onChange={guardChange(setPageMode)}
            disabled={disabled}
          />
        </div>
        <div className={classnames(styles.controlPanelLimit, styles.control)}>
          <H4>Table size</H4>
          <Toggler
            selected={tableSize}
            options={tableSizeOptions}
            onToggle={guardChange(setTableSize)}
            disabled={disabled}
            className={classnames(styles.gameTogglerColor, styles.firstToggler)}
            controlToggler
          />
          <Select
            className={styles.adaptiveSelectComponent}
            value={tableSizeOptions?.find(option => option.value === tableSize) || null}
            options={tableSizeOptions}
            onChange={guardChange(setTableSize)}
            disabled={disabled}
          />
        </div>
        <div className={classnames(styles.controlPanelLimit, styles.control)}>
          <H4>Stack size</H4>
          <Toggler
            selected={stackSize}
            options={stackSizeOptions}
            onToggle={guardChange(setStackSize)}
            disabled={disabled}
            className={classnames(styles.gameTogglerColor, styles.firstToggler)}
            controlToggler
          />
          <Select
            className={styles.adaptiveSelectComponent}
            value={stackSizeOptions?.find(option => option.value === stackSize) || null}
            options={stackSizeOptions}
            onChange={guardChange(setStackSize)}
            disabled={disabled}
          />
        </div>
        {showHandTypeSelect && (
          <div className={classnames(styles.controlPanelLimit, styles.control)}>
            <H4>Hand Type</H4>
            <Select
              value={selectedHandType}
              options={handTypeOptions}
              onChange={guardChange(setSelectedHandType)}
              disabled={disabled}
              placeholder="Any hand type"
            />
          </div>
        )}
        {isResultsMode ? (
          <>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>Position Type</H4>
              <Toggler
                selected={resultsPositionType}
                options={resultsPositionTypeOptions}
                onToggle={guardChange(setResultsPositionType)}
                disabled={disabled}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                controlToggler
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={resultsPositionType}
                options={resultsPositionTypeOptions}
                onChange={guardChange(setResultsPositionType)}
                disabled={disabled}
              />
            </div>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>PFR</H4>
              <Toggler
                selected={resultsPfrType}
                options={resultsPfrTypeOptions}
                onToggle={guardChange(setResultsPfrType)}
                disabled={disabled}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                controlToggler
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={resultsPfrType}
                options={resultsPfrTypeOptions}
                onChange={guardChange(setResultsPfrType)}
                disabled={disabled}
              />
            </div>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>Position</H4>
              <Toggler
                selected={resultsPlayerPosition}
                options={resultsPlayerPositionOptions}
                onToggle={guardChange(setResultsPlayerPosition)}
                disabled={disabled}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                controlToggler
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={resultsPlayerPosition}
                options={resultsPlayerPositionOptions}
                onChange={guardChange(setResultsPlayerPosition)}
                disabled={disabled}
              />
            </div>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>Spot</H4>
              <div className={styles.resultsSpotWrapper}>
                <Multicheckbox
                  options={resultsPositionOptions}
                  selectedOptions={resultsPosition?.value ? [resultsPosition.value.split('_')[0]] : []}
                  setSelectedOptions={guardChange(setResultsPosition)}
                  forTrickyCards
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>Position</H4>
              <Toggler
                selected={positionType}
                options={positionTypeOptions}
                onToggle={guardChange(setPositionType)}
                disabled={disabled}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                controlToggler
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={positionTypeOptions.find((item) => item.value === positionType) || null}
                options={positionTypeOptions}
                onChange={guardChange(setPositionType)}
                disabled={disabled}
              />
              {tableSize !== 'HU' && (
                <Select
                  className={styles.positionSelectCompact}
                  value={selectedPositionOption}
                  options={positionOptions}
                  onChange={guardChange(setPosition)}
                  disabled={disabled}
                />
              )}
            </div>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>Board Suit</H4>
              <Toggler
                selected={boardSuit}
                options={boardSuitOptions}
                onToggle={guardChange(setBoardSuit)}
                disabled={disabled}
                className={classnames(styles.gameTogglerColor, styles.firstToggler)}
                boardTypeIsPaired={boardType === 'paired'}
                controlToggler
              />
              <Select
                className={styles.adaptiveSelectComponent}
                value={boardSuitOptions?.find(option => option.value === boardSuit) || null}
                options={boardSuitOptions}
                onChange={guardChange(setBoardSuit)}
                disabled={disabled}
              />
            </div>
          </>
        )}
        <div className={classnames(styles.controlPanelLimit, styles.control)}>
          <H4>Board Type</H4>
          <Toggler
            selected={boardType}
            options={boardTypeOptions}
            onToggle={guardChange(setBoardType)}
            disabled={disabled}
            className={classnames(styles.gameTogglerColor, styles.firstToggler)}
            boardSuitIsMono={!isResultsMode && boardSuit === 'mono'}
            controlToggler
          />
          <Select
            className={styles.adaptiveSelectComponent}
            value={boardTypeOptions?.find(option => option.value === boardType) || null}
            options={boardTypeOptions}
            onChange={guardChange(setBoardType)}
            disabled={disabled}
          />
        </div>
        {!isResultsMode && (
          <>
            <div className={classnames(styles.controlPanelLimit, styles.control)}>
              <H4>High Card</H4>
              <Toggler
                selected={selectedHighCard}
                options={highCardOptions}
                onToggle={guardChange(setSelectedHighCard)}
                disabled={disabled}
                className={classnames(styles.gameTogglerColor, styles.highCardToggler)}
                controlToggler
              />
            </div>
            <div className={styles.checkboxesWrapper}>
              <Multicheckbox
                options={boardOptions}
                selectedOptions={selectedBoard}
                setSelectedOptions={guardChange(setSelectedBoard)}
                forPreflop
                forTrickyCards
              />
            </div>
          </>
        )}
      </fieldset>
    </Card>
  )
};

export default ControlPanel;
